import { FC, useEffect, useState } from "react";
import { Box, Grid, Popover, Stack, Typography, styled } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { Button } from "@/components/elements/Button";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { BulkSyncComboboxMultiField } from "@/features/learner/components/learner-list/BulkSyncComboboxMultiField";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { IconCheck } from "@/assets/icons";
import { background, divider, state, text } from "@/theme/colors";
import { EMPTY_BULK_SYNC_FILTER, TBulkSyncFilterForm, hasBulkSyncFilter } from "@/features/learner/components/learner-list/bulk-sync-filter";
import {
  SKILL_ONE_PERIOD_RANGE_ERROR,
  getSkillOnePeriodErrors,
  isSkillOnePeriodRangeValid,
  isSkillOnePeriodValid,
} from "@/features/learner/components/learner-list/bulk-sync-filter-schema";
import { useBulkSyncFilterMasterData } from "@/features/learner/components/learner-list/use-bulk-sync-filter-master-data";

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  // 「条件を設定」押下時に確定した条件を親へ返す。
  onApply: (filters: TBulkSyncFilterForm) => void;
  // 親が保持中の確定条件 (再オープン時に復元する)。
  initialFilters: TBulkSyncFilterForm;
  // 会社アカウントは所属機関の条件を持たない (RSOのみ表示)。
  isCompanyUser?: boolean;
};

// ラベル付きフィールドの共通枠。
const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack spacing={0.5}>
    <Typography variant="cap12Light">{label}</Typography>
    {children}
  </Stack>
);

// 「〜」で繋ぐ日付レンジ。
const DateRange: FC<{
  from: TBulkSyncFilterForm[keyof TBulkSyncFilterForm];
  to: TBulkSyncFilterForm[keyof TBulkSyncFilterForm];
  onChangeFrom: (_v: unknown) => void;
  onChangeTo: (_v: unknown) => void;
}> = ({ from, to, onChangeFrom, onChangeTo }) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Box sx={{ flex: 1 }}>
      <InputDatePicker placeholder="選択してください" value={from as never} onChange={onChangeFrom} nonControl />
    </Box>
    <Typography variant="cap12" sx={{ color: text.secondary }}>
      〜
    </Typography>
    <Box sx={{ flex: 1 }}>
      <InputDatePicker placeholder="選択してください" value={to as never} onChange={onChangeTo} nonControl />
    </Box>
  </Stack>
);

// 1号経過期間の「年 / か月」1組。from / to で共通利用する。error はスキーマ由来のメッセージを受け取る。
const SkillOnePeriodRange: FC<{
  year: string;
  month: string;
  yearError?: string;
  monthError?: string;
  onChangeYear: (_v: string) => void;
  onChangeMonth: (_v: string) => void;
}> = ({ year, month, yearError, monthError, onChangeYear, onChangeMonth }) => (
  <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ flex: 1 }}>
    <Box sx={{ flex: 1 }}>
      <Input
        nonControl
        style={{ width: "100%" }}
        transformToHalfWidth
        value={year}
        error={!!yearError}
        helperText={yearError}
        onChange={(e) => onChangeYear((e.target as HTMLInputElement).value)}
      />
    </Box>
    <Typography variant="cap12" sx={{ color: text.secondary, pt: "10px" }}>
      年
    </Typography>
    <Box sx={{ flex: 1 }}>
      <Input
        nonControl
        style={{ width: "100%" }}
        transformToHalfWidth
        value={month}
        error={!!monthError}
        helperText={monthError}
        onChange={(e) => onChangeMonth((e.target as HTMLInputElement).value)}
      />
    </Box>
    <Typography variant="cap12" sx={{ color: text.secondary, pt: "10px" }}>
      ヶ月
    </Typography>
  </Stack>
);

export const BulkSyncLearnerFilterPanel: FC<Props> = ({
  anchorEl,
  onClose,
  onApply,
  initialFilters,
  isCompanyUser = false,
}) => {
  const open = Boolean(anchorEl);
  const methods = useForm(); // 子コンポーネントの useFormContext() を満たすためのダミー。
  const [values, setValues] = useState<TBulkSyncFilterForm>(initialFilters);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  // 分野 / 国籍 はシステムの master データ、在籍状況 / 性別 は enum 由来の選択肢。
  const { industryOptions, nationalityOptions, enrollmentStatusOptions, genderOptions, isLoadingIndustry, isLoadingNationality } =
    useBulkSyncFilterMasterData();

  // 再オープン時に確定済みの条件を復元する。
  useEffect(() => {
    if (open) setValues(initialFilters);
  }, [open, initialFilters]);

  const set = <K extends keyof TBulkSyncFilterForm>(key: K, value: TBulkSyncFilterForm[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  // exam-steps / learning-progress と同様、クリアは確認モーダルを挟む。
  const handleOpenClearConfirm = () => {
    if (hasBulkSyncFilter(values)) setIsClearConfirmOpen(true);
  };
  const handleClearConfirm = () => {
    setValues(EMPTY_BULK_SYNC_FILTER);
    setIsClearConfirmOpen(false);
  };

  // 1号経過期間: スキーマからフィールド別 error を取得し、error 表示と canApply を同一ソースにする。
  const skillOnePeriodErrors = getSkillOnePeriodErrors(values);
  const canApply = isSkillOnePeriodValid(values);

  const handleApply = () => {
    if (!canApply) return;
    onApply(values);
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      sx={{
        "& .MuiPaper-root": {
          // 縦位置はトリガーに追従し、横位置のみ画面中央に寄せる。
          left: "50% !important",
          transform: "translateX(-50%) !important",
          mt: 1,
          borderRadius: "8px",
          boxShadow: "0px 8px 24px 0px #00000026",
        },
      }}
    >
      <FormProvider {...methods}>
        <Container>
          <ScrollableContent>
            <Grid container rowSpacing={2} columnSpacing={3}>
              {/* Row 1 */}
              <Grid item xs={4}>
                <Field label="管理番号">
                  <Input
                    nonControl
                    placeholder="入力してください"
                    style={{ width: "100%" }}
                    value={values.managementNumber}
                    onChange={(e) => set("managementNumber", (e.target as HTMLInputElement).value)}
                  />
                </Field>
              </Grid>
              <Grid item xs={4}>
                <BulkSyncComboboxMultiField
                  label="在籍状況"
                  options={enrollmentStatusOptions}
                  value={values.employmentStatus}
                  onChange={(v) => set("employmentStatus", v)}
                />
              </Grid>
              {!isCompanyUser && (
                <Grid item xs={4}>
                  <Field label="所属機関">
                    <Input
                      nonControl
                      placeholder="入力してください"
                      style={{ width: "100%" }}
                      value={values.companyName}
                      onChange={(e) => set("companyName", (e.target as HTMLInputElement).value)}
                    />
                  </Field>
                </Grid>
              )}

              {/* Row 2 */}
              <Grid item xs={4}>
                <Field label="外国人名">
                  <Input
                    nonControl
                    placeholder="入力してください"
                    style={{ width: "100%" }}
                    value={values.fullName}
                    onChange={(e) => set("fullName", (e.target as HTMLInputElement).value)}
                  />
                </Field>
              </Grid>
              <Grid item xs={4}>
                <BulkSyncComboboxMultiField
                  label="分野"
                  options={industryOptions}
                  loading={isLoadingIndustry}
                  value={values.industryNames}
                  onChange={(v) => set("industryNames", v)}
                />
              </Grid>
              <Grid item xs={4}>
                <BulkSyncComboboxMultiField
                  label="国籍"
                  options={nationalityOptions}
                  loading={isLoadingNationality}
                  value={values.nationalityNames}
                  onChange={(v) => set("nationalityNames", v)}
                />
              </Grid>

              <Grid item xs={4}>
                <Field label="性別">
                  <Select
                    placeholder="選択してください"
                    style={{ width: "100%" }}
                    options={genderOptions}
                    value={values.gender}
                    onChange={(v) => set("gender", v ?? "")}
                  />
                </Field>
              </Grid>
              <Grid item xs={8}></Grid>
              <Grid item xs={8}>
                <Field label="入社日">
                  <DateRange
                    from={values.hiringDateFrom}
                    to={values.hiringDateTo}
                    onChangeFrom={(v) => set("hiringDateFrom", v as TBulkSyncFilterForm["hiringDateFrom"])}
                    onChangeTo={(v) => set("hiringDateTo", v as TBulkSyncFilterForm["hiringDateTo"])}
                  />
                </Field>
              </Grid>

              <Grid item xs={8}>
                <Field label="在留期限">
                  <DateRange
                    from={values.residenceExpiryFrom}
                    to={values.residenceExpiryTo}
                    onChangeFrom={(v) => set("residenceExpiryFrom", v as TBulkSyncFilterForm["residenceExpiryFrom"])}
                    onChangeTo={(v) => set("residenceExpiryTo", v as TBulkSyncFilterForm["residenceExpiryTo"])}
                  />
                </Field>
              </Grid>

              <Grid item xs={8}>
                <Field label="1号経過期間">
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <SkillOnePeriodRange
                      year={values.skillOnePeriodYearFrom}
                      month={values.skillOnePeriodMonthFrom}
                      yearError={skillOnePeriodErrors.skillOnePeriodYearFrom}
                      monthError={skillOnePeriodErrors.skillOnePeriodMonthFrom}
                      onChangeYear={(v) => set("skillOnePeriodYearFrom", v)}
                      onChangeMonth={(v) => set("skillOnePeriodMonthFrom", v)}
                    />
                    <Typography variant="cap12" sx={{ color: text.secondary, pt: "10px" }}>
                      〜
                    </Typography>
                    <SkillOnePeriodRange
                      year={values.skillOnePeriodYearTo}
                      month={values.skillOnePeriodMonthTo}
                      yearError={skillOnePeriodErrors.skillOnePeriodYearTo}
                      monthError={skillOnePeriodErrors.skillOnePeriodMonthTo}
                      onChangeYear={(v) => set("skillOnePeriodYearTo", v)}
                      onChangeMonth={(v) => set("skillOnePeriodMonthTo", v)}
                    />
                  </Stack>
                  {!isSkillOnePeriodRangeValid(values) && (
                    <Typography variant="cap12" sx={{ color: state.error_1, pt: 0.5 }}>
                      {SKILL_ONE_PERIOD_RANGE_ERROR}
                    </Typography>
                  )}

                </Field>
              </Grid>
            </Grid>
          </ScrollableContent>

          <Footer>
            <Stack direction="row" gap="16px">
              <Button variant="ghost" onClick={onClose} size="sm">
                キャンセル
              </Button>
              <Button
                variant="text"
                onClick={handleOpenClearConfirm}
                size="sm"
                disabled={!hasBulkSyncFilter(values)}
              >
                クリア
              </Button>
            </Stack>
            <Button onClick={handleApply} size="sm" disabled={!canApply} startIcon={<IconCheck width={20} height={20} />}>
              条件を設定
            </Button>
          </Footer>
        </Container>
      </FormProvider>

      <ConfirmDeleteModal
        open={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onOk={handleClearConfirm}
        message="設定した条件がすべてクリアされますがよろしいですか？"
        title=""
      />
    </Popover>
  );
};

const Container = styled(Box)`
  width: 1056px;
  max-width: calc(100vw - 48px);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: ${background.white};
  border-radius: 8px;
`;

const ScrollableContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Footer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid ${divider.middle};
`;
